/* eslint-disable @typescript-eslint/no-explicit-any */
import api from './api';

const BASE = '/promotions';

export interface PromotionListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  type?: string;
  channel?: string;
}

export interface VoucherListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  voucher_type?: string;
  source?: string;
}

export interface FlashSaleListParams {
  page?: number;
  limit?: number;
  state?: string;
  search?: string;
}

export interface WholesaleGiftListParams {
  page?: number;
  limit?: number;
  status?: string;
}

export interface ReportParams {
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
}

export interface PromotionPayload {
  name: string;
  code?: string;
  type: string;
  description?: string;
  slug?: string;
  image_url?: string;
  store_ids?: string[];
  schedule: {
    start_date: string;
    end_date?: string;
    timezone?: string;
    recurring?: Record<string, unknown>;
  };
  actions: Array<{
    type: string;
    discount_percent?: number;
    discount_amount?: number;
    max_discount_amount?: number;
    free_product_id?: string;
    free_product_quantity?: number;
    target?: string;
    target_product_ids?: string[];
    target_category_ids?: string[];
  }>;
  conditions?: Record<string, unknown>;
  limits?: Record<string, unknown>;
  status?: string;
  stackable?: Record<string, unknown>;
  bxgy_config?: Record<string, unknown>;
  bundle_config?: Record<string, unknown>;
  tier_config?: Array<{ tier: string; discount_percent: number }>;
  gold_price_lock_config?: Record<string, unknown>;
}

export interface VoucherPayload {
  code: string;
  name: string;
  voucher_type: string;
  description?: string;
  discount_amount?: number;
  discount_percent?: number;
  max_discount_amount?: number;
  gift_product_id?: string;
  gift_quantity?: number;
  gift_description?: string;
  source?: string;
  valid_from: string;
  valid_until?: string;
  total_limit?: number;
  max_uses_per_customer?: number;
  max_uses_per_day?: number;
  total_budget?: number;
  target_channels?: string[];
  target_store_ids?: string[];
  target_customer_ids?: string[];
  is_exclusive?: boolean;
  display_on_checkout?: boolean;
}

export interface FlashSalePayload {
  name: string;
  description?: string;
  banner_image_url?: string;
  start_time: string;
  end_time: string;
  timezone?: string;
  store_ids?: string[];
  channels?: string[];
  max_orders?: number;
  show_countdown?: boolean;
  show_stock_remaining?: boolean;
  featured?: boolean;
}

export interface LoyaltyProgramPayload {
  is_active: boolean;
  earning_rate: number;
  redemption_rate: number;
  min_points_redeem?: number;
  expiry_months?: number;
  description?: string;
}

export interface LoyaltyTierPayload {
  name: string;
  min_points: number;
  multiplier: number;
  benefits?: string[];
}

export interface WholesaleGiftPayload {
  name: string;
  description?: string;
  min_order_amount?: number;
  gift_items: Array<{
    product_id: string;
    allocated_quantity: number;
  }>;
  valid_from: string;
  valid_until: string;
  status?: string;
}

export const promotionService = {
  // Promotions CRUD
  list: (params: PromotionListParams) =>
    api.get(`${BASE}/v2`, { params }).then((r: any) => r.data),
  create: (data: PromotionPayload) =>
    api.post(`${BASE}/v2`, data).then((r: any) => r.data),
  detail: (id: string) =>
    api.get(`${BASE}/v2/${id}`).then((r: any) => r.data),
  update: (id: string, data: Partial<PromotionPayload>) =>
    api.patch(`${BASE}/v2/${id}`, data).then((r: any) => r.data),
  delete: (id: string) =>
    api.delete(`${BASE}/v2/${id}`).then((r: any) => r.data),
  activate: (id: string) =>
    api.post(`${BASE}/v2/${id}/activate`).then((r: any) => r.data),
  pause: (id: string) =>
    api.post(`${BASE}/v2/${id}/pause`).then((r: any) => r.data),
  clone: (id: string) =>
    api.post(`${BASE}/v2/${id}/clone`).then((r: any) => r.data),
  usage: (id: string) =>
    api.get(`${BASE}/v2/${id}/usage`).then((r: any) => r.data),
  evaluate: (data: Record<string, unknown>) =>
    api.post(`${BASE}/evaluate`, data).then((r: any) => r.data),

  // Vouchers
  listVouchers: (params: VoucherListParams) =>
    api.get(`${BASE}/v2/vouchers`, { params }).then((r: any) => r.data),
  createVoucher: (data: VoucherPayload) =>
    api.post(`${BASE}/v2/vouchers`, data).then((r: any) => r.data),
  detailVoucher: (id: string) =>
    api.get(`${BASE}/v2/vouchers/${id}`).then((r: any) => r.data),
  updateVoucher: (id: string, data: Partial<VoucherPayload>) =>
    api.patch(`${BASE}/v2/vouchers/${id}`, data).then((r: any) => r.data),
  deleteVoucher: (id: string) =>
    api.delete(`${BASE}/v2/vouchers/${id}`).then((r: any) => r.data),
  activateVoucher: (id: string) =>
    api.post(`${BASE}/v2/vouchers/${id}/activate`).then((r: any) => r.data),
  validateVoucher: (data: { code: string; items: Array<{ product_id: string; quantity: number; unit_price: number }>; customer_id?: string; channel?: string }) =>
    api.post(`${BASE}/vouchers/validate`, data).then((r: any) => r.data),
  applyVoucher: (data: { code: string; session_id: string; items: Array<{ product_id: string; quantity: number; unit_price: number }>; customer_id?: string; channel?: string }) =>
    api.post(`${BASE}/vouchers/apply`, data).then((r: any) => r.data),
  removeVoucher: (data: { code: string; session_id: string }) =>
    api.post(`${BASE}/vouchers/remove`, data).then((r: any) => r.data),
  createVoucherCondition: (voucherId: string, data: any) =>
    api.post(`${BASE}/v2/vouchers/${voucherId}/conditions`, data).then((r: any) => r.data),

  // Flash Sales
  getActiveFlashSales: (params?: { channel?: string; store_id?: string }) =>
    api.get(`${BASE}/v2/flash-sales/active`, { params }).then((r: any) => r.data),
  listFlashSales: (params: FlashSaleListParams) =>
    api.get(`${BASE}/v2/flash-sales`, { params }).then((r: any) => r.data),
  createFlashSale: (data: FlashSalePayload) =>
    api.post(`${BASE}/v2/flash-sales`, data).then((r: any) => r.data),
  detailFlashSale: (id: string) =>
    api.get(`${BASE}/v2/flash-sales/${id}`).then((r: any) => r.data),
  updateFlashSale: (id: string, data: Partial<FlashSalePayload>) =>
    api.patch(`${BASE}/v2/flash-sales/${id}`, data).then((r: any) => r.data),
  deleteFlashSale: (id: string) =>
    api.delete(`${BASE}/v2/flash-sales/${id}`).then((r: any) => r.data),
  activateFlashSale: (id: string) =>
    api.post(`${BASE}/v2/flash-sales/${id}/activate`).then((r: any) => r.data),
  pauseFlashSale: (id: string) =>
    api.post(`${BASE}/v2/flash-sales/${id}/pause`).then((r: any) => r.data),
  reserveStock: (id: string, data: { item_id: string; quantity: number; customer_id: string }) =>
    api.post(`${BASE}/v2/flash-sales/${id}/reserve`, data).then((r: any) => r.data),

  // Loyalty
  getLoyaltyProgram: () =>
    api.get(`${BASE}/v2/loyalty/program`).then((r: any) => r.data),
  updateLoyaltyProgram: (data: LoyaltyProgramPayload) =>
    api.put(`${BASE}/v2/loyalty/program`, data).then((r: any) => r.data),
  getLoyaltyBalance: (customerId: string) =>
    api.get(`${BASE}/v2/loyalty/balance/${customerId}`).then((r: any) => r.data),
  redeemPoints: (data: { customer_id: string; points: number; order_id?: string }) =>
    api.post(`${BASE}/v2/loyalty/redeem`, data).then((r: any) => r.data),
  listTiers: () =>
    api.get(`${BASE}/v2/loyalty/tiers`).then((r: any) => r.data),
  createTier: (data: LoyaltyTierPayload) =>
    api.post(`${BASE}/v2/loyalty/tiers`, data).then((r: any) => r.data),
  updateTier: (id: string, data: Partial<LoyaltyTierPayload>) =>
    api.patch(`${BASE}/v2/loyalty/tiers/${id}`, data).then((r: any) => r.data),

  deleteTier: (id: string) =>
    api.delete(`${BASE}/v2/loyalty/tiers/${id}`).then((r: any) => r.data),

  // Wholesale Gifts
  listWholesaleGifts: (params: WholesaleGiftListParams) =>
    api.get(`${BASE}/v2/wholesale-gifts`, { params }).then((r: any) => r.data),
  createWholesaleGift: (data: WholesaleGiftPayload) =>
    api.post(`${BASE}/v2/wholesale-gifts`, data).then((r: any) => r.data),
  detailWholesaleGift: (id: string) =>
    api.get(`${BASE}/v2/wholesale-gifts/${id}`).then((r: any) => r.data),
  updateWholesaleGift: (id: string, data: Partial<WholesaleGiftPayload>) =>
    api.patch(`${BASE}/v2/wholesale-gifts/${id}`, data).then((r: any) => r.data),
  deleteWholesaleGift: (id: string) =>
    api.delete(`${BASE}/v2/wholesale-gifts/${id}`).then((r: any) => r.data),
  approveWholesaleGift: (id: string) =>
    api.post(`${BASE}/v2/wholesale-gifts/${id}/approve`).then((r: any) => r.data),

  // Reports
  reportPerformance: (params: ReportParams) =>
    api.get(`${BASE}/v2/reports/performance`, { params }).then((r: any) => r.data),
  reportVoucherUsage: (params: ReportParams) =>
    api.get(`${BASE}/v2/reports/voucher-usage`, { params }).then((r: any) => r.data),
  reportFlashSale: (params: ReportParams) =>
    api.get(`${BASE}/v2/reports/flash-sale`, { params }).then((r: any) => r.data),
  reportLoyalty: () =>
    api.get(`${BASE}/v2/reports/loyalty`).then((r: any) => r.data),
  reportROI: (id: string) =>
    api.get(`${BASE}/v2/reports/roi/${id}`).then((r: any) => r.data),
};
